/**
 * Direct-from-browser typing indicator publisher.
 *
 * The server-side typing Lambda is now a no-op stub — typing is ephemeral
 * and high-frequency, so funneling it through a Lambda + duplicate fanout
 * was pure waste. Instead the browser holds a scoped Momento token with
 * publish-only permission for `prefix.typing.{conversationId}` and writes
 * directly.
 *
 * Receivers subscribe to the matching topic on conversation open
 * (see [[useTypingTopicSubscription]]).
 */

import { setChatTypingPublisher, useUserStore } from "@slumbr/core";

import { getMomentoTopicPrefix, publishToTopic } from "@/apps/messenger/momento-subscriber";

// Throttle typing-start events to once per 5 s per conversation so a long
// continuous typing burst doesn't flood the topic. Typing-stop / send-on
// -submit always pass through immediately.
const TYPING_START_THROTTLE_MS = 5_000;
const lastSentAt = new Map<string, number>();

export function registerTypingPublisher() {
  setChatTypingPublisher(async (conversationId, isTyping) => {
    if (!conversationId) return;
    const now = Date.now();
    if (isTyping) {
      const last = lastSentAt.get(conversationId) ?? 0;
      if (now - last < TYPING_START_THROTTLE_MS) return;
      lastSentAt.set(conversationId, now);
    } else {
      lastSentAt.delete(conversationId);
    }
    try {
      // The typing grant is per-conversation (membership-verified server
      // side), so publish with a token scoped to this conversation — the
      // app-level token has no `typing.*` permission. Same scope key as the
      // receiver's subscription, so both share one cached token.
      const scope = { conversationIds: [conversationId] };
      const prefix = await getMomentoTopicPrefix(scope);
      const senderId = useUserStore.getState().user?.id ?? "";
      if (!senderId) return;
      const displayName = useUserStore.getState().user?.displayName ?? "Someone";
      await publishToTopic(`${prefix}.typing.${conversationId}`, {
        type: "chat.typing",
        appId: "messenger",
        conversationId,
        senderId,
        displayName,
        isTyping,
        timestamp: new Date().toISOString(),
      }, scope);
    } catch (err) {
      console.warn("[typing] direct publish failed", err);
    }
  });
}
